import React, { useEffect } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import parsePhoneNumberFromString from 'libphonenumber-js';

import { PinInput } from 'shared/components/inputs/PinInput/PinInput';
import { useForm, useWatch } from 'react-hook-form';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import {
  BodyText,
  SmsOptInModalBodyContainer,
  SmsOptInModalFooterContainer,
} from '../Shared/styles';
import { Button } from '../../../../Button/Button';
import {
  StrongText,
  VerificationForm,
  VerifyingSpinnerContainer,
  VerifyingText,
} from './VerificationBody.styles';

export interface VerificationPinFormInput {
  pin: string;
}
type Props = {
  handleSendPhoneVerificationCode: () => void;
  timeLeft: number;
  setTimeLeft: React.Dispatch<React.SetStateAction<number>>;
  verificationLoading: boolean;
  handleVerifyPin: (pin: string) => void;
  phoneNumber: string | undefined;
  verificationError: string | null;
};

export function VerificationBody({
  handleSendPhoneVerificationCode,
  timeLeft,
  setTimeLeft,
  verificationLoading,
  handleVerifyPin,
  phoneNumber,
  verificationError,
}: Props) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    clearErrors,
  } = useForm<VerificationPinFormInput>();
  const codeExpired = timeLeft <= 0;

  const pinError = errors.pin?.message;
  const watchPin = useWatch({
    control,
    name: 'pin',
  });
  const [pinChanged, setPinChanged] = React.useState(false);
  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (timeLeft > 0) {
      timerId = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => clearInterval(timerId);
  }, [timeLeft, setTimeLeft]);

  useEffect(() => {
    if (!pinChanged && !verificationLoading) {
      if (verificationError) {
        setError('pin', { message: verificationError });
      }
    }
  }, [verificationLoading, verificationError, setError, pinChanged]);

  const handlePinEntered = (pin: string) => {
    setPinChanged(true);
    setValue('pin', pin);
  };
  const handlePinChange = (pin: string) => {
    setPinChanged(true);
    setValue('pin', pin);
    clearErrors('pin');
  };

  const handleSubmitForm = async (formInput: VerificationPinFormInput) => {
    if (!watchPin) {
      setError('pin', { message: t('account.smsOptIn.verificationModal.noCode') });
      return;
    }

    if (watchPin.length < 6) {
      setError('pin', { message: t('account.smsOptIn.verificationModal.wrongCode') });
      return;
    }

    handleVerifyPin(formInput.pin);
    setPinChanged(false);
  };
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  return (
    <VerificationForm onSubmit={handleSubmit(handleSubmitForm)}>
      <SmsOptInModalBodyContainer>
        <BodyText as="p">
          <Trans
            i18nKey="account.smsOptIn.verificationModal.weSent"
            components={{
              strong: <StrongText as="p" />,
            }}
            values={{
              phoneNumber: phoneNumber
                ? parsePhoneNumberFromString(phoneNumber, 'US')?.formatNational()
                : '',
            }}
          />
        </BodyText>
        <BodyText as="p">{t('account.smsOptIn.verificationModal.enterCode')}</BodyText>
        <PinInput
          onPinEntered={handlePinEntered}
          onChange={handlePinChange}
          hasError={Boolean(pinError)}
          errorMessage={pinError}
        />
        <BodyText as="p" $margin="16px 0 0 0">
          <Trans>
            {codeExpired
              ? t('verification.codeHasExpired')
              : t('verification.codeWillExpireIn', { time: formatTime(timeLeft) })}
          </Trans>
        </BodyText>
      </SmsOptInModalBodyContainer>
      {verificationLoading && (
        <VerifyingSpinnerContainer>
          <LoadingSpinner localCenter />

          <VerifyingText as="p" $textAlign="center" $margin="0 0 16px 0">
            {t('account.smsOptIn.verificationModal.verifying')}
          </VerifyingText>
        </VerifyingSpinnerContainer>
      )}

      <SmsOptInModalFooterContainer>
        <Button
          styleVariant="tertiary"
          label={t('account.smsOptIn.verificationModal.resend')}
          onClick={handleSendPhoneVerificationCode}
        />
        <Button
          styleVariant="primary"
          label={t('account.smsOptIn.verificationModal.verify')}
          type="submit"
        />
      </SmsOptInModalFooterContainer>
    </VerificationForm>
  );
}
export default VerificationBody;
